import { Injectable } from '@angular/core';
import { InfoAlertComponent } from '../components/info-dialog/info-alert.component';
import { ErrorDialogComponent } from '../components/error-dialog/error-dialog.component';
import { TranslatePipe } from '../translate/pipes/translate.pipe';
import { ExitDialogComponent } from '../components/exit-dialog/exit-dialog.component';
import { AnimationService } from './animation.service';
import { ModalController } from '@ionic/angular';
import { MultiModalButton, MultiModalCheckbox, MultiModalComponent, MultiModalDataset, MultiModalMessage } from '../components/multi-modal/multi-modal.component';
import { LightboxModalComponent } from '../components/lightbox-modal/lightbox-modal.component';
import { NameRequiredModalComponent } from '../components/name-required-modal/name-required-modal.component';

@Injectable({
    providedIn: 'root',
})
export class ModalService {

    infoAlertComponent: any;
    errorAlertComponent: any;
    exitAlertComponent: any;
    currentHelpModal: HTMLIonModalElement | null = null;

    constructor(
        private modalController: ModalController,
        private tp: TranslatePipe,
        private animationService: AnimationService,
    ) { }

    setAlertComponent(component: any) {
        if (component instanceof InfoAlertComponent) {
            this.infoAlertComponent = component;
        } else if (component instanceof ErrorDialogComponent) {
            this.errorAlertComponent = component;
        } else if (component instanceof ExitDialogComponent) {
            this.exitAlertComponent = component;
        }
    }

    async showAlert(message: string, alertType: string = 'error', header: string = 'ERROR_TITLE', navigate?: boolean) {
        if (alertType === 'info') {
            this.infoAlertComponent.header = this.tp.transform(header);
            this.infoAlertComponent.message = this.tp.transform(message!);

            return await this.infoAlertComponent.presentAlert();
        } else if (alertType === 'error') {
            this.errorAlertComponent.header = this.tp.transform(header);
            this.errorAlertComponent.message = this.tp.transform(message!);

            return await this.errorAlertComponent.presentAlert(navigate);
        } else if (alertType === 'exit') {
            this.exitAlertComponent.header = this.tp.transform(header);
            this.exitAlertComponent.message = this.tp.transform(message!);

            return await this.exitAlertComponent.presentAlert();
        }

        return false;
    }

    async showHelpAndExplanationModal(msg: string, title: string, type: 'explanation' | 'hint', learningTaskType: string, quizId: string, quizTitle: string, allowImages: boolean = true) {
        const { HelpAndExplanationDialogComponent } = await import('./help-and-explanation-dialog/help-and-explanation-dialog.component'); // to handle circular import. beacuse the modal service is used in markdown-wrapper. and this dialog also uses markdown-wrapper

        const cssClassType = type === 'hint' ? 'info-alert-hint' : 'info-alert-explanation'
        const modal = await this.modalController.create({
            component: HelpAndExplanationDialogComponent,
            componentProps: {
                text: msg,
                title: title,
                type: type,
                learningTaskType: learningTaskType,
                quizId: quizId,
                quizTitle: quizTitle,
                allowImages: allowImages
            },
            backdropDismiss: true,
            cssClass: cssClassType,
            enterAnimation: this.animationService.opacityModalEnterAnimation,
            leaveAnimation: this.animationService.opacityModalLeaveAnimation,
        });

        this.currentHelpModal = modal;

        const popStateHandler = async (event: any) => {
            await modal.dismiss();
        };

        window.addEventListener('popstate', popStateHandler);

        modal.onDidDismiss().then(() => {
            window.removeEventListener('popstate', popStateHandler);
        });

        await modal.present();
    }

    async dismissHelpModal() {
        if (this.currentHelpModal) {
            try {
                await this.currentHelpModal.dismiss();
                this.currentHelpModal = null;
            } catch (error) {
                console.error('Error dismissing help modal:', error);
            }
        }
    }

    async dismissCurrentModal() {
        try {
            const modal = await this.modalController.getTop();
            if (modal) {
                await modal.dismiss();
            }
        } catch (error) {
            console.error('Error dismissing current modal:', error);
        }
    }

    async showMultiModal(title?: string, message?: MultiModalMessage, dataset?: MultiModalDataset, buttons?: MultiModalButton[], shadowBackDrop: boolean = false, gap?: string, containerStyle?: string, backdropDismiss: boolean = false, logo?: string, messageBeforeLogo?: MultiModalMessage, checkbox?: MultiModalCheckbox) {
        const modal = await this.modalController.create({
            component: MultiModalComponent,
            canDismiss: true,
            cssClass: 'multi-modal',
            componentProps: {
                buttons: buttons,
                title: title,
                message: message,
                dataset: dataset,
                gap: gap,
                containerStyle: containerStyle,
                logo: logo,
                messageBeforeLogo: messageBeforeLogo,
                checkbox: checkbox
            },
            showBackdrop: shadowBackDrop,
            backdropDismiss: backdropDismiss,
            enterAnimation: this.animationService.opacityModalEnterAnimation,
            leaveAnimation: this.animationService.opacityModalLeaveAnimation,
        });

        const popStateHandler = async (event: any) => {
            await modal.dismiss();
        };

        window.addEventListener('popstate', popStateHandler);

        modal.onDidDismiss().then(() => {
            window.removeEventListener('popstate', popStateHandler);
        });

        await modal.present()
        const { data } = await modal.onDidDismiss<{ result: any, role: string | null, error: null | string }>();
        return data;
    }

    async showNameRequiredModal(): Promise<{ name?: string; logout?: boolean }> {
        while (true) {
            const modal = await this.modalController.create({
                component: NameRequiredModalComponent,
                canDismiss: async (data?: { name?: string; logout?: boolean }) => !!data?.name || !!data?.logout,
                cssClass: 'name-required-modal',
                backdropDismiss: false,
                showBackdrop: true,
                enterAnimation: this.animationService.opacityModalEnterAnimation,
                leaveAnimation: this.animationService.opacityModalLeaveAnimation,
            });

            const popStateHandler = (event: PopStateEvent) => {
                history.pushState(null, '', window.location.href);
            };
            history.pushState(null, '', window.location.href);
            window.addEventListener('popstate', popStateHandler);

            await modal.present();
            const { data } = await modal.onDidDismiss<{ name?: string; logout?: boolean }>();

            window.removeEventListener('popstate', popStateHandler);

            if (data?.logout) {
                return { logout: true };
            }

            const name = data?.name ? String(data.name).trim() : '';
            if (name.length >= 3) {
                return { name };
            }
        }
    }

    async showLightboxModal(imgURL: string, showBackdrop = true, backdropDismiss = false) {
        const modal = await this.modalController.create({
            component: LightboxModalComponent,
            canDismiss: true,
            cssClass: 'lightbox-modal',
            componentProps: {
                imgURL: imgURL,
            },
            showBackdrop: showBackdrop,
            backdropDismiss: backdropDismiss,
            animated: false,
        });

        const popStateHandler = async (event: any) => {
            await modal.dismiss();
        };

        window.addEventListener('popstate', popStateHandler);

        modal.onDidDismiss().then(() => {
            window.removeEventListener('popstate', popStateHandler);
        });

        await modal.present()
        await modal.onDidDismiss();
    }

}
